import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  type Signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { EmptyStateComponent } from '@principle-theorem/ng-principle-shared';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import { ReferralPortalLink } from '@principle-theorem/principle-core';

enum ReferralAccessIssue {
  WrongEmail = 'wrongEmail',
  NotAvailable = 'notAvailable',
  Unknown = 'unknown',
}

/**
 * Shown when the portal cannot load a referral for the signed-in user. The
 * lookup only matches the email the referral was sent to. When the link
 * carries that address (`expectedEmail`), the screen can say for certain
 * whether the recipient signed in with the wrong one; older links without it
 * fall back to a general explanation.
 */
@Component({
  selector: 'pr-referral-portal-access-issue',
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatIconModule,
    EmptyStateComponent,
  ],
  templateUrl: './referral-portal-access-issue.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralPortalAccessIssueComponent {
  readonly accessIssue = ReferralAccessIssue;
  readonly email = input<string>();
  readonly expectedEmail = input<string>();
  readonly switchAccount = output<void>();

  readonly issue: Signal<ReferralAccessIssue> = computed(() => {
    const email = this.email();
    const expectedEmail = this.expectedEmail();
    if (!email || !expectedEmail) {
      return ReferralAccessIssue.Unknown;
    }
    return ReferralPortalLink.isDifferentEmail(expectedEmail, email)
      ? ReferralAccessIssue.WrongEmail
      : ReferralAccessIssue.NotAvailable;
  });
}
